import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { FeedbackService, HttpErrorService, HttpErrorServiceEvent } from '@app/core/services';
import { environment } from 'environments/environment';

const isProduction = environment.production;

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor, OnDestroy {
	errorMessage: string;
	private readonly destroy$ = new Subject();
	private override = false;
	private readonly standardError = 'Oops, something went wrong. If the problem continues, please contact support.';
	private catchError = true;

	constructor(private readonly feedbackService: FeedbackService, private readonly httpErrorService: HttpErrorService) {
		this.resetMessage();
		this.httpErrorService.watcher$.pipe(takeUntil(this.destroy$)).subscribe((event: HttpErrorServiceEvent) => {
			switch (event.action) {
				case 'override':
					this.overrideMessage(event.message);
					break;
				case 'suppress':
					this.suppressErrorMessage();
					break;
			}
		});
	}

	public overrideMessage(message: string): void {
		this.override = true;
		this.errorMessage = message;
	}

	public suppressErrorMessage(): void {
		this.catchError = false;
	}

	/**
	 * Only return the API error message when the status is 4## or
	 * when the application is not running in production. 500 errors
	 * are hidden in production. Production mode is turned on by default.
	 *
	 * @param errorResponse HttpErrorResponse
	 * @returns string - error message
	 */
	public computeMessage(errorResponse: HttpErrorResponse, productionFlag = true): string {
		// Override trumps all other options
		if (this.override) {
			return this.errorMessage;
		}

		const serverErrorMessage = errorResponse.error?.ExceptionMessage || errorResponse.error?.Message || errorResponse.statusText;
		const displayServerErrorMessage = +(errorResponse.status / 100).toFixed() === 4;

		// Not in production, display any error from the server regardless of the status number
		if (!productionFlag) {
			return serverErrorMessage || this.errorMessage;
		}

		return serverErrorMessage && displayServerErrorMessage ? serverErrorMessage : this.errorMessage;
	}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(request).pipe(
			catchError((errorResponse: HttpErrorResponse) => {
				// Allow developer to flip the auto feedback error off
				// For example when dealing with async blob response.
				if (this.catchError) {
					const message = this.computeMessage(errorResponse, isProduction);
					this.feedbackService.error(message);
				}

				return throwError(() => errorResponse);
			}),
			finalize(() => {
				// Reset all settings after each request to avoid suppressing all error
				this.resetMessage();
				this.resetSuppressor();
			})
		);
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	private resetMessage() {
		this.errorMessage = this.standardError;
	}

	private resetSuppressor() {
		this.catchError = true;
	}
}
